import React from 'react';
import StatusBadge from './StatusBadge';

/**
 * EmptyState - Unified empty state component with helpful messages
 */
const EmptyState = ({ variant = 'no-selection', onAction, actionLabel }) => {
  const states = {
    'no-selection': {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="9" y1="9" x2="15" y2="15"></line>
          <line x1="15" y1="9" x2="9" y2="15"></line>
        </svg>
      ),
      title: 'No Transcript Selected',
      message: 'Select a transcript from the list to view details',
      showAction: false
    },
    'no-results': {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          <line x1="8" y1="11" x2="14" y2="11"></line>
        </svg>
      ),
      title: 'No Transcripts Found',
      message: 'Click "Sync Now" to fetch your recent Google Meet transcripts',
      showAction: true
    },
    'processing': {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5" className="spin-slow">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      title: 'Transcript Processing',
      message: 'This transcript is still being processed by Google. Transcripts typically become available 15-45 minutes after a meeting ends.',
      hint: 'Try syncing again in a few minutes',
      showAction: true
    },
    'unavailable': {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      ),
      title: 'Transcript Unavailable',
      message: 'This meeting does not have a transcript available.',
      hints: [
        'Transcription may not have been enabled for this meeting',
        'The transcript may have been deleted',
        'You may not have permission to access this transcript'
      ],
      showAction: false
    },
    'no-notes': {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="12" y1="18" x2="12" y2="12"></line>
          <line x1="9" y1="15" x2="15" y2="15"></line>
        </svg>
      ),
      title: 'No Meeting Notes',
      message: 'No Google meeting notes were found for this session.',
      hints: [
        'Enable "Take notes for me" in Google Meet settings',
        'Notes are created by Google\'s AI during the meeting',
        'Try refreshing to check for newly available notes'
      ],
      showAction: true
    },
    'not-connected': {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
          <polyline points="10 17 15 12 10 7"></polyline>
          <line x1="15" y1="12" x2="3" y2="12"></line>
        </svg>
      ),
      title: 'Not Connected',
      message: 'Connect your Google account to sync meeting transcripts',
      showAction: true
    }
  };

  const state = states[variant] || states['no-selection'];

  return (
    <div className={`empty-state empty-state--${variant}`}>
      <div className="empty-state-icon">{state.icon}</div>
      <h3 className="empty-state-title">{state.title}</h3>
      <p className="empty-state-message">{state.message}</p>

      {state.hint && (
        <p className="empty-state-hint">
          <StatusBadge variant="info" size="small">Tip</StatusBadge>
          {state.hint}
        </p>
      )}

      {state.hints && state.hints.length > 0 && (
        <ul className="empty-state-hints">
          {state.hints.map((hint, index) => (
            <li key={index}>{hint}</li>
          ))}
        </ul>
      )}

      {state.showAction && onAction && (
        <button className="btn btn-primary" onClick={onAction}>
          {actionLabel || 'Take Action'}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
